import React, { useState, useEffect, useRef } from 'react';
import { t } from '@konrad/reweb-aem';
import cx from 'classnames';
import { BREAKPOINT_MOBILE } from '../../lib/constants';
import LinkBase from '../_shared/Link/LinkBase';
import TabContent from '../_shared/TabContent';
import MobileSlider from '../_shared/MobileSlider/MobileSlider';
import Text from '../_shared/Text/Text';
import Button from '../_shared/Button/Button';
import Icon from '../_shared/Icon/Icon';
import TabService from '../../lib/services/tabService';
import styles from './LaunchPad.module.scss';
import useBreakpoint from '../../lib/helpers/useBreakpoint';
import LeftArrowIcon from './LeftArrowIcon';

const TAB_CONTAINER_ID = 'launchpad-tabs';
const LAUNCH_PAD_TAB_LABELS = [
    t('fs.launchpad.featured'),
    t('fs.launchpad.loans'),
    t('fs.launchpad.home-equity-loans'),
    t('fs.launchpad.retail-financing'),
    t('fs.launchpad.tools-and-resources'),
];

const LaunchPadCardDefault = (props) => {
    const { ctaText, ctaLink, description, backgroundImage, linkType, title, targetId } = props;

    return (
        <div
            style={{ ...(backgroundImage && { backgroundImage: `url('${backgroundImage}')` }) }}
            className={cx(styles['card-container'], styles['default'])}
        >
            <div className={styles['card-container-content']}>
                <Text
                    className={cx(styles['card-container-title-default'])}
                    content={title}
                    size="x-large"
                />
                <Text
                    className={cx(styles['card-container-description'])}
                    size="large"
                    content={description}
                />
            </div>
            <Button
                showExternalIcon={false}
                linkText={ctaText}
                linkType={linkType}
                altText={ctaText}
                disabled={false}
                linkUrl={ctaLink}
                buttonStyle="primary-variant"
                className={styles['card-container-button']}
                targetId={targetId}
            />
        </div>
    );
};

const LaunchPadCardVariant = (props) => {
    const { ctaText, ctaLink, description, title, linkType } = props;

    return (
        <div className={cx(styles['card-container'], styles['variant'])}>
            <Text className={cx(styles['card-container-title'])} content={title} size="x-large" />
            <Text
                className={cx(styles['card-container-variant-description'])}
                content={description}
                size="large"
            />
            <LinkBase
                addIdSuffix={true}
                altText={ctaText}
                className={cx(styles.link)}
                target={linkType}
                url={ctaLink}
            >
                <span className={cx(styles['link-text-wrapper'])}>
                    {ctaText}
                    <LeftArrowIcon />
                </span>
            </LinkBase>
        </div>
    );
};

const LaunchPadCard = (props) => {
    const { cardType = 'type1', ...cardProps } = props;
    const CardComponent = cardType === 'type1' ? LaunchPadCardVariant : LaunchPadCardDefault;

    return <CardComponent {...cardProps} />;
};

const CardsContainer = (props) => {
    const { children, setShouldExpand, shouldExpand, displayShowMore, mobileNavigationRef } = props;
    const onViewMoreClick = () => {
        setShouldExpand(!shouldExpand);

        if (shouldExpand) {
            mobileNavigationRef.current.scrollIntoView({ behavior: 'instant' });
        }
    };

    return (
        <div className={styles['tab-wrapper']}>
            <div className={styles['cards-container']}>{children}</div>
            {displayShowMore && (
                <div className={styles['view-more-wrapper']}>
                    <Icon
                        className={cx(styles['indicator-icon'], {
                            [styles['active']]: shouldExpand,
                        })}
                        size="xx-small"
                        url="/etc.clientlibs/fs/clientlibs/clientlib-base/resources/icons/chevron-up-purple.svg"
                    />
                    <div onClick={onViewMoreClick}>
                        {shouldExpand ? t('fs.launchpad.view-less') : t('fs.launchpad.view-more')}
                    </div>
                </div>
            )}
        </div>
    );
};

const DesktopCardsContainer = (props) => {
    const { label, children } = props;

    return (
        <div className={cx('tabs-wrapper', styles.root)}>
            <div
                id={TAB_CONTAINER_ID}
                aria-label={label}
                className={cx('tab-container', styles.tablist)}
                role="tablist"
            >
                {children}
            </div>
            <div className={cx('tab-panels-container')} />
        </div>
    );
};

const LaunchPad = (props) => {
    const { items1, items2, items3, items4, items5 } = props;
    const tabs = [items1, items2, items3, items4, items5].filter((items) => items?.length);
    const labels = LAUNCH_PAD_TAB_LABELS.filter((label, i) => i < tabs.length);
    const currentBreakpoint = useBreakpoint();
    const isMobile = currentBreakpoint === BREAKPOINT_MOBILE;
    const [shouldExpand, setShouldExpand] = useState(false);
    const mobileNavigationRef = useRef(null);

    useEffect(() => {
        if (isMobile) return;

        TabService.createTabContainer(TAB_CONTAINER_ID);

        // setTimeout 0 is to execute the function in a following iteration of the browser's event loop
        setTimeout(() => {
            if (tabs.length > 1) {
                window.tabContainers[TAB_CONTAINER_ID].setActiveTab('tab1');
            }
        }, 0);
    }, [tabs, isMobile]);

    return isMobile ? (
        <MobileSlider labels={labels} mobileNavigationRef={mobileNavigationRef}>
            {tabs.map((cards) => {
                const cardsToRender = cards.filter((card, index) => shouldExpand || index < 3);

                return (
                    <CardsContainer
                        displayShowMore={cardsToRender.length >= 3}
                        setShouldExpand={setShouldExpand}
                        shouldExpand={shouldExpand}
                        mobileNavigationRef={mobileNavigationRef}
                    >
                        {cardsToRender.map((card, i) => (
                            <LaunchPadCard
                                key={`${card.title}-${i}`}
                                title={card.title}
                                ctaText={card.ctaText}
                                ctaLink={card.urlPath}
                                description={card.description}
                                cardType={card.cardType}
                                backgroundImage={card.backgroundImageReference}
                                linkType={card.linkType}
                                targetId={card.targetId}
                            />
                        ))}
                    </CardsContainer>
                );
            })}
        </MobileSlider>
    ) : (
        <DesktopCardsContainer>
            {tabs.map((cards, i) => (
                <TabContent
                    key={`tab${i}`}
                    tabId={`tab${i}`}
                    title={labels[i]}
                    className={styles['tab-content']}
                >
                    <div className={styles['launchpad-cards-tab']}>
                        {cards.map((card, i) => (
                            <LaunchPadCard
                                title={card.title}
                                key={`${card.title}-${i}`}
                                ctaText={card.ctaText}
                                ctaLink={card.urlPath}
                                description={card.description}
                                cardType={card.cardType}
                                backgroundImage={card.backgroundImageReference}
                                linkType={card.linkType}
                                targetId={card.targetId}
                            />
                        ))}
                    </div>
                </TabContent>
            ))}
        </DesktopCardsContainer>
    );
};

LaunchPad.displayName = 'LaunchPad';

export default LaunchPad;
